import React from 'react';

import { ToggleSwitch } from 'Views/Components/New/ToggleSwitch';
import {
	CommunicationChannelKey,
	CommunicationChannelState,
	CommunicationPreferenceRow,
} from '../Types/CommunicationPreferences';

export interface PreferenceTableProps {
	rows: CommunicationPreferenceRow[];
	/** The value to render, which for a staged edit is not what the server last said. */
	valueOf: (row: CommunicationPreferenceRow, channel: CommunicationChannelKey) => boolean;
	onChange: (row: CommunicationPreferenceRow, channel: CommunicationChannelKey, enabled: boolean) => void;
	/** Blocks every control — a save in flight, or a viewer without permission. */
	disabled?: boolean;
	/**
	 * SMS controls are inert because the master switch is down or there is no number on file. They
	 * render off and disabled rather than hidden, so it is visible what would come back.
	 */
	smsMuted?: boolean;
	/**
	 * Marks the rows worth keeping on. Shown to the customer and to staff alike — it is a label on
	 * what the message is, not a judgement about who is reading it.
	 */
	markRecommended?: boolean;
	/**
	 * Whether the application sends SMS at all. The column is shown whenever it does, even with no
	 * template enabled anywhere — those rows render an em dash, which is the honest answer.
	 */
	showSms?: boolean;
}

/**
 * A message that cannot be switched off. Shown in place of the control it would otherwise disable,
 * because a switch that is on and cannot be moved reads as broken rather than as deliberate — and a
 * line of small print under every second row said the same thing several times over.
 */
function RequiredBadge() {
	return (
		<span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
			Required
		</span>
	);
}

/**
 * Shown beside the row's name, on both views.
 *
 * <p>Advice, not a rule — the switch still works. It marks the messages about something the reader is
 * holding and relying on, so switching one off is a decision rather than something noticed later:
 * a customer who turns off cancellation emails and then misses one, or a staff member doing it on
 * their behalf.</p>
 */
function RecommendedBadge() {
	return (
		<span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
			Recommended
		</span>
	);
}

/**
 * Whether to flag this row to staff.
 *
 * <p>The row has to be worth flagging <i>and</i> have something staff can act on. Trip updates is
 * neither theirs nor the customer's to change, so it already says Required in both columns and a
 * badge beside the name would be advice about a control that is not there.</p>
 */
function isRecommended(row: CommunicationPreferenceRow) {
	return (
		row.recommended
		&& ([row.email, row.sms] as CommunicationChannelState[]).some(
			state => state.available && state.editable,
		)
	);
}

function ChannelCell({
	row,
	channel,
	state,
	checked,
	disabled,
	muted,
	onChange,
}: {
	row: CommunicationPreferenceRow;
	channel: CommunicationChannelKey;
	state: CommunicationChannelState;
	checked: boolean;
	disabled: boolean;
	muted: boolean;
	onChange: (enabled: boolean) => void;
}) {
	if (!state.offered || !state.available) {
		// An em dash rather than an empty cell, so the column still reads as a column and it is clear
		// the channel is unavailable rather than merely switched off. No explanation beside it: the
		// reason is an operator setting, and spelling it out in a grid of switches added a line of
		// small print to rows that have nothing the reader can do about it.
		return <span className="text-sm text-gray-400">—</span>;
	}

	// Locked on and staying on. Staff can edit these rows, so they get the switch instead.
	if (!state.editable && checked && !muted) {
		return <RequiredBadge />;
	}

	return (
		<ToggleSwitch
			size="sm"
			aria-label={`${row.name} by ${channel === 'email' ? 'email' : 'SMS'}`}
			checked={checked && !muted}
			disabled={!state.editable || disabled || muted}
			onChange={event => onChange(event.target.checked)}
			data-test-id={`communication-preference-${row.key}-${channel}`}
		/>
	);
}

/**
 * The message-by-channel grid, shared by the customer's own screen and the staff view of it.
 *
 * Presentational on purpose: which controls are editable, and which channels can send at all, are
 * decided by the server and arrive on each row. Recomputing either here would put the two out of
 * step the first time a rule changed.
 */
export default function PreferenceTable({
	rows,
	valueOf,
	onChange,
	disabled = false,
	smsMuted = false,
	markRecommended = true,
	showSms = false,
}: PreferenceTableProps) {
	return (
		<div className="overflow-x-auto">
			<table className="w-full text-left">
				<thead>
					<tr className="border-b border-gray-200">
						<th className="py-2 text-xs font-semibold uppercase text-gray-500">Message</th>
						<th className="w-24 py-2 text-xs font-semibold uppercase text-gray-500">Email</th>
						{showSms && <th className="w-24 py-2 text-xs font-semibold uppercase text-gray-500">SMS</th>}
					</tr>
				</thead>
				<tbody>
					{rows.map(row => (
						<tr key={row.key} className="border-b border-gray-100 align-top">
							<td className="py-3 pr-4">
								<div className="flex flex-wrap items-center gap-2">
									<span className="text-sm font-medium">{row.name}</span>
									{markRecommended && isRecommended(row) && <RecommendedBadge />}
								</div>
								<div className="text-sm text-gray-600">{row.description}</div>
							</td>
							<td className="py-3">
								<ChannelCell
									row={row}
									channel="email"
									state={row.email}
									checked={valueOf(row, 'email')}
									disabled={disabled}
									muted={false}
									onChange={enabled => onChange(row, 'email', enabled)}
								/>
							</td>
							{showSms && (
								<td className="py-3">
									<ChannelCell
										row={row}
										channel="sms"
										state={row.sms}
										checked={valueOf(row, 'sms')}
										disabled={disabled}
										muted={smsMuted}
										onChange={enabled => onChange(row, 'sms', enabled)}
									/>
								</td>
							)}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
